import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PhotoCameraBackOutlinedIcon from '@mui/icons-material/PhotoCameraBackOutlined';

import { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import CommonDropdown from '../../../../components/common-dropdown/CommonDropdown';
import SearchInput from '../../../../components/common-inputs/SearchInput';
import TooltipButton from '../../../../components/tooltip-button/TooltipButton';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import { CUSTOM_IMAGE, MODIFY_CONCEPT, RUN_AGAIN } from '../../../../redux/id8/id8Actions';
import { changeImagesTable } from '../../../../redux/id8/id8Reducer';
import useImagesGenerationStatus from '../../../hooks/useImagesGenerationStatus';
import PromptModal, { PROMPT_MODAL_TYPE } from '../prompt-modal/PromptModal';
import useConditionTooltip from '../../../../hooks/useConditionTooltip';
import { useProject } from '../../../../context/ProjectContext';
import { ID8_GEN_TYPES } from '../../../../constants';

import scss from './FrameFilters.module.scss';

function FrameFilters() {
  const dispatch = useDispatch();
  const { t } = useTranslation('id8');
  const { id } = useParams();

  const { actions } = useProject();

  const [activeModal, setActiveModal] = useState(null);

  const imagesTableSettings = useSelector((state) => state.id8.imagesTableSettings);
  const loadingActions = useSelector((state) => state.id8.loadingActions);
  const imagesContent = useSelector((state) => state.id8.imagesContent);
  const isImagesGenerating = useImagesGenerationStatus({ genType: ID8_GEN_TYPES.IMAGES });

  const buttonsTooltip = useConditionTooltip([
    {
      condition: imagesContent.length === 0,
      message: t('noImages'),
    },
    {
      condition: isImagesGenerating,
      message: t('imagesGenerating'),
    },
  ]);

  const isActionButtonsDisabled = imagesContent.length === 0 || isImagesGenerating;

  const modalConfig = {
    [PROMPT_MODAL_TYPE.MODIFY]: {
      title: t('addYourChanges'),
      onSubmit: async (values) => {
        actions('modifyConcept', {
          newConcept: values.description,
        });
      },
    },
    [PROMPT_MODAL_TYPE.CUSTOM]: {
      title: t('addYourIdea'),
      onSubmit: async (values) => {
        actions('customImage', { description: values.description, projectId: id });
      },
    },
  };

  const handleChangeSearch = (newSearch) => {
    dispatch(changeImagesTable({ searchQuery: newSearch, page: 1 }));
  };

  const handleChangeSize = (newSize) => {
    dispatch(changeImagesTable({ size: newSize }));
  };

  const handleRunAgain = () => {
    actions('runAgain', { id });
  };

  const handleOpenModal = (modalType) => () => {
    setActiveModal(modalType);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleModalSubmit = async (values) => {
    await modalConfig[activeModal].onSubmit(values);
    handleCloseModal();
  };

  useEffect(() => {
    actions('getImages', id);
  }, [
    actions,
    id,
    imagesTableSettings.searchQuery,
    imagesTableSettings.page,
    imagesTableSettings.sort,
    imagesTableSettings.size,
  ]);

  return (
    <div className={scss.wrapper}>
      <SearchInput value={imagesTableSettings.searchQuery} onChange={handleChangeSearch} size='sm' maxWidth={364} />

      <div className={scss.rightSection}>
        <div className={scss.buttonsWrapper}>
          <TooltipButton
            styleColor={BUTTON_STYLES.PRIMARY}
            onClick={handleRunAgain}
            disabled={isActionButtonsDisabled}
            isLoading={loadingActions[RUN_AGAIN]}
            tooltip={buttonsTooltip}
            maxWidth={192}
            LeftIcon={<LoopRoundedIcon />}>
            {t('runAgain')}
          </TooltipButton>

          <TooltipButton
            styleColor={BUTTON_STYLES.PRIMARY}
            onClick={handleOpenModal(PROMPT_MODAL_TYPE.MODIFY)}
            disabled={isActionButtonsDisabled}
            isLoading={loadingActions[MODIFY_CONCEPT]}
            tooltip={buttonsTooltip}
            maxWidth={192}
            LeftIcon={<DrawOutlinedIcon />}>
            {t('modifyConcept')}
          </TooltipButton>

          <TooltipButton
            styleColor={BUTTON_STYLES.PRIMARY}
            onClick={handleOpenModal(PROMPT_MODAL_TYPE.CUSTOM)}
            disabled={isActionButtonsDisabled}
            isLoading={loadingActions[CUSTOM_IMAGE]}
            tooltip={buttonsTooltip}
            maxWidth={192}
            LeftIcon={<PhotoCameraBackOutlinedIcon />}>
            {t('customImage')}
          </TooltipButton>
        </div>

        <WithLabel variant={LABEL_STYLES.ROW} label={t('show')} maxWidth={168}>
          <CommonDropdown
            value={imagesTableSettings.size}
            onSelect={handleChangeSize}
            options={IMAGES_PAGE_SIZE_OPTIONS}
          />
        </WithLabel>
      </div>

      <PromptModal
        isOpen={!!activeModal}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onSubmit={handleModalSubmit}
        title={modalConfig[activeModal]?.title}
      />
    </div>
  );
}

export default FrameFilters;

export const IMAGES_PAGE_SIZE_OPTIONS = [
  {
    value: 8,
    label: '8',
  },
  {
    value: 16,
    label: '16',
  },
  {
    value: 32,
    label: '32',
  },
];
